/**
 * UUIDv7, minted in the browser.
 *
 * The client creates the id of everything it creates — a card has to appear
 * under the cursor in the same frame the user releases it, long before the
 * server has heard of it. The server accepts the id it is given, and because
 * the id travels with the mutation, a resent create can never become a
 * duplicate.
 *
 * Same layout as the backend's generator (RFC 9562): a 48-bit millisecond
 * timestamp, then a 12-bit counter that keeps ids minted in the same
 * millisecond in order, then random bits. Time-ordered ids also mean a sort by
 * id is a sort by creation time, which the UI uses as a stable tie-break.
 */

let lastTimestamp = -1;
let counter = 0;

const MAX_COUNTER = 0x0fff;

export function uuidv7(): string {
  let timestamp = Date.now();

  if (timestamp === lastTimestamp) {
    counter += 1;
    if (counter > MAX_COUNTER) {
      timestamp = lastTimestamp + 1;
      lastTimestamp = timestamp;
      counter = 0;
    }
  } else if (timestamp > lastTimestamp) {
    lastTimestamp = timestamp;
    counter = 0;
  } else {
    // The clock moved backwards; hold the previous timestamp so ids stay ordered.
    timestamp = lastTimestamp;
    counter += 1;
  }

  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);

  bytes[0] = Math.floor(timestamp / 2 ** 40) & 0xff;
  bytes[1] = Math.floor(timestamp / 2 ** 32) & 0xff;
  bytes[2] = Math.floor(timestamp / 2 ** 24) & 0xff;
  bytes[3] = Math.floor(timestamp / 2 ** 16) & 0xff;
  bytes[4] = Math.floor(timestamp / 2 ** 8) & 0xff;
  bytes[5] = timestamp & 0xff;
  bytes[6] = 0x70 | ((counter >>> 8) & 0x0f);
  bytes[7] = counter & 0xff;
  bytes[8] = 0x80 | ((bytes[8] ?? 0) & 0x3f);

  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20, 32)}`;
}
